import React from "react";
import { Helmet } from "react-helmet-async";

// SITE_URL priority:
//  1) REACT_APP_SITE_URL (explicit canonical, if set at build time)
//  2) window.location.origin at runtime (so preview shares unfurl the preview host)
//  3) https://www.socibaba.com as final fallback (SSR/build safe)
const ENV_SITE_URL =
  (typeof process !== "undefined" &&
    process.env &&
    (process.env.REACT_APP_SITE_URL as string | undefined)) ||
  "";
const RUNTIME_ORIGIN =
  typeof window !== "undefined" && window.location && window.location.origin
    ? window.location.origin
    : "";
const SITE_URL = (ENV_SITE_URL || RUNTIME_ORIGIN || "https://www.socibaba.com").replace(
  /\/$/,
  ""
);
const CANONICAL_SITE_URL = "https://www.socibaba.com";
const DEFAULT_TITLE =
  "SociBaba — AI Product Intelligence, SIBA Score & AI Cart™";
const DEFAULT_DESC =
  "SociBaba is the AI product intelligence + honest-commerce platform. Intent Radar surfaces real buying signals, Sancta answers with real data, AI Cart™ compares choices side-by-side, and the SibaScore (0-1000) — computed from 11 trust signals — is the rating buyers finally believe in.";
const DEFAULT_KEYWORDS = [
  "AI product intelligence",
  "SIBA Score",
  "product trust rating",
  "AI Cart",
  "SociBaba Intent Radar",
  "Sancta AI",
  "conversational commerce",
  "honest reviews",
  "SociBaba",
  "shop AI",
  "product discovery",
  "brand trust score",
];
// OG image defaults to the CANONICAL prod host so social crawlers (which don't
// execute JS) fetch the same image regardless of whether the share came from
// preview or production. Callers can still override with a fully-qualified URL
// (e.g. product-specific OG images) — see the `image` prop below.
const DEFAULT_OG_IMAGE = `${CANONICAL_SITE_URL}/og-cover.png`;

export interface SEOProps {
  title?: string;
  description?: string;
  keywords?: string[];
  path?: string;
  image?: string;
  type?: "website" | "article" | "product";
  noindex?: boolean;
  jsonLd?: Record<string, unknown> | Record<string, unknown>[];
}

const buildCanonical = (path?: string) => {
  // Canonical always resolves to the production site so SEO signals consolidate,
  // regardless of preview host that served the page.
  if (!path) return CANONICAL_SITE_URL + "/";
  if (/^https?:\/\//i.test(path)) return path;
  return CANONICAL_SITE_URL + (path.startsWith("/") ? path : "/" + path);
};

const buildOgUrl = (path?: string) => {
  // og:url reflects the live host so shares unfurl to the exact page the visitor sees.
  if (!path) return SITE_URL + "/";
  if (/^https?:\/\//i.test(path)) return path;
  return SITE_URL + (path.startsWith("/") ? path : "/" + path);
};

export const SEO: React.FC<SEOProps> = ({
  title,
  description,
  keywords,
  path,
  image,
  type = "website",
  noindex = false,
  jsonLd,
}) => {
  const fullTitle = title
    ? `${title} · SociBaba`
    : DEFAULT_TITLE;
  const desc = description || DEFAULT_DESC;
  const canonical = buildCanonical(path);
  const ogUrl = buildOgUrl(path);
  const kw = (keywords && keywords.length ? keywords : DEFAULT_KEYWORDS).join(", ");
  const rawImage = image || DEFAULT_OG_IMAGE;
  // If a caller passes a relative path (e.g. "/product/xyz.png"), resolve it
  // against the canonical prod host so crawlers on any share fetch the same URL.
  const ogImage = /^https?:\/\//i.test(rawImage)
    ? rawImage
    : `${CANONICAL_SITE_URL}${rawImage.startsWith("/") ? "" : "/"}${rawImage}`;

  const structuredDataArray = Array.isArray(jsonLd)
    ? jsonLd
    : jsonLd
    ? [jsonLd]
    : [];

  return (
    <Helmet prioritizeSeoTags>
      <title>{fullTitle}</title>
      <meta name="description" content={desc} />
      <meta name="keywords" content={kw} />
      <link rel="canonical" href={canonical} />
      <meta
        name="robots"
        content={
          noindex
            ? "noindex, nofollow"
            : "index, follow, max-image-preview:large, max-snippet:-1"
        }
      />
      <meta name="author" content="SociBaba" />
      <meta name="application-name" content="SociBaba" />
      <meta name="theme-color" content="#0F0F10" />

      {/* Open Graph */}
      <meta property="og:type" content={type} />
      <meta property="og:site_name" content="SociBaba" />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={desc} />
      <meta property="og:url" content={ogUrl} />
      <meta property="og:image" content={ogImage} />
      <meta property="og:image:secure_url" content={ogImage} />
      <meta property="og:image:type" content="image/png" />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:image:alt" content="SociBaba — AI Social Intelligence Workspace" />
      <meta property="og:locale" content="en_US" />

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:site" content="@socibaba" />
      <meta name="twitter:creator" content="@socibaba" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={desc} />
      <meta name="twitter:image" content={ogImage} />

      {structuredDataArray.map((data, i) => (
        <script type="application/ld+json" key={`ld-${i}`}>
          {JSON.stringify(data)}
        </script>
      ))}
    </Helmet>
  );
};

// Reusable JSON-LD payloads exported for convenience
export const organizationJsonLd = {
  "@context": "https://schema.org",
  "@type": "Organization",
  name: "SociBaba",
  legalName: "SOCIBABA",
  url: CANONICAL_SITE_URL,
  logo: `${CANONICAL_SITE_URL}/logo.png`,
  identifier: [
    { "@type": "PropertyValue", propertyID: "CIN", value: "U74999KA2020PTC134970" },
    { "@type": "PropertyValue", propertyID: "GSTIN", value: "29ABDCS7875E1ZU" },
  ],
  address: {
    "@type": "PostalAddress",
    streetAddress: "#122, First Floor, 3rd Cross, Chowdeshwari Nagara, Hosa Byrohalli, Archakara Layout 2nd Stage, Sulikere",
    addressLocality: "Bengaluru",
    addressRegion: "Karnataka",
    postalCode: "560060",
    addressCountry: "IN",
  },
  sameAs: [
    "https://twitter.com/socibaba",
    "https://www.linkedin.com/company/socibaba",
    "https://www.instagram.com/socibaba",
    "https://www.youtube.com/@socibaba",
    "https://www.tiktok.com/@socibaba",
    "https://www.facebook.com/socibaba",
  ],
  contactPoint: [
    {
      "@type": "ContactPoint",
      email: "support@socibaba.com",
      telephone: "+91-9739959565",
      contactType: "customer support",
      areaServed: "IN",
      availableLanguage: ["English", "Hindi"],
    },
  ],
};

export const websiteJsonLd = {
  "@context": "https://schema.org",
  "@type": "WebSite",
  name: "SociBaba",
  url: CANONICAL_SITE_URL,
  potentialAction: {
    "@type": "SearchAction",
    target: `${CANONICAL_SITE_URL}/discovery?q={search_term_string}`,
    "query-input": "required name=search_term_string",
  },
};

export const softwareAppJsonLd = {
  "@context": "https://schema.org",
  "@type": "SoftwareApplication",
  name: "SociBaba",
  applicationCategory: "BusinessApplication",
  operatingSystem: "Web",
  url: CANONICAL_SITE_URL,
  offers: {
    "@type": "Offer",
    price: "0",
    priceCurrency: "INR",
    description: "Free tier available. Pro plans start at ₹499/mo.",
  },
  // NOTE: aggregateRating deliberately omitted — we do not have real,
  // aggregated user reviews yet, and Google penalizes fabricated ratings.
  // Add it back once /api/site-config/public exposes a computed real value.
  description: DEFAULT_DESC,
};

export const SITE = { url: CANONICAL_SITE_URL, host: SITE_URL };
